
import React, { Component, ReactNode, ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    console.error("ErrorBoundary - getDerivedStateFromError:", error);
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught error:", error);
    console.error("Error info:", errorInfo);
    console.error("Component stack:", errorInfo.componentStack);
    console.error("Error boundary location:", errorInfo);
    
    // Store error info in state for debugging
    this.setState({ 
      hasError: true, 
      error, 
      errorInfo 
    });
  }

  public render() {
    if (this.state.hasError) {
      console.error("ErrorBoundary rendering error state", {
        error: this.state.error,
        errorInfo: this.state.errorInfo
      });
      
      return this.props.fallback || (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md" role="alert">
          <h3 className="text-red-800 font-medium">Application Error Detected</h3>
          <p className="text-red-600 text-sm mt-1">
            {this.state.error?.message || "An unexpected error occurred"}
          </p>
          <details className="mt-2 text-xs text-red-500">
            <summary>Error Details (for debugging)</summary>
            <pre className="mt-1 whitespace-pre-wrap">
              {this.state.error?.stack}
            </pre>
            {this.state.errorInfo && (
              <pre className="mt-1 whitespace-pre-wrap">
                Component Stack: {this.state.errorInfo.componentStack}
              </pre>
            )}
          </details>
          <button 
            onClick={() => {
              console.log("Attempting to reset error boundary");
              this.setState({ hasError: false, error: undefined, errorInfo: undefined });
            }}
            className="mt-2 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            aria-label="Try to recover from error"
          >
            Try Again
          </button>
          <button 
            onClick={() => {
              console.log("Forcing page reload");
              window.location.reload();
            }}
            className="mt-2 ml-2 px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
            aria-label="Reload page"
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
